import { addDays, addMinutes, addYears, formatISO, isAfter, isBefore, set } from 'date-fns'
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import { deliveriesApi } from '@/api/client'
import type { ClosingTime, VirtualBrand } from '@/types/virtualBrands'

import { useConfigStore } from './config'

export const useDeliveryCompaniesStore = defineStore('deliveryCompanies', () => {
  const configStore = useConfigStore()

  const { config } = storeToRefs(configStore)

  const virtualBrandsClosingTimes = ref<VirtualBrand[]>([])

  const sortedClosedUntil = computed(() => {
    return virtualBrandsClosingTimes.value
      .flatMap(brand => Object.values(brand.externalDeliveryCompaniesClosingTimes))
      .filter(time => time !== null)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
  })

  const virtualBrandsWithClosedDeliveries = computed(() => {
    return virtualBrandsClosingTimes.value
      .map(brand => {
        return {
          ...brand,
          externalDeliveryCompaniesClosingTimes: Object.entries(
            brand.externalDeliveryCompaniesClosingTimes
          ).reduce(
            (res, [key, value]) => {
              if (value && isAfter(new Date(value), new Date())) {
                res[key] = value
              }
              return res
            },
            {} as Record<string, string>
          )
        }
      })
      .filter(brand => Object.values(brand.externalDeliveryCompaniesClosingTimes).length > 0)
  })

  const bookingsBrand = computed(() => {
    const brand = virtualBrandsClosingTimes.value.find(
      brand => 'Bookings' in brand.externalDeliveryCompaniesClosingTimes
    )
    if (!brand) return null
    return {
      id: brand.id,
      brandName: brand.brandName,
      closedUntil: brand.externalDeliveryCompaniesClosingTimes['Bookings']
    }
  })

  const hasDeliveries = computed(() => {
    return (
      virtualBrandsClosingTimes.value.filter(
        brand => Object.values(brand.externalDeliveryCompaniesClosingTimes).length > 0
      ).length > 0
    )
  })

  function refreshClosingTimes(newVirtualBrandsClosingTimes: VirtualBrand[]) {
    virtualBrandsClosingTimes.value = newVirtualBrandsClosingTimes
  }

  async function openDeliveries(deliveriesToOpen: Record<string, string[]>) {
    const { failed } = await deliveriesApi.openDeliveries({
      openDeliveriesRequest: { deliveriesToOpen }
    })
    const failedKeys = new Set(failed.map(f => `${f.brandId}:${f.company}`))
    for (const [brandId, companies] of Object.entries(deliveriesToOpen)) {
      for (const company of companies) {
        if (failedKeys.has(`${brandId}:${company}`)) continue
        const targetBrand = virtualBrandsClosingTimes.value.find(brand => brand.id === brandId)
        if (targetBrand) {
          targetBrand.externalDeliveryCompaniesClosingTimes[company] = null
        }
      }
    }
    return { failed }
  }

  async function closeDeliveries(
    deliveriesToClose: Record<string, string[]>,
    closingTime: ClosingTime
  ) {
    let closedUntil
    if (!isNaN(closingTime as number)) {
      closedUntil = addMinutes(new Date(), closingTime as number)
    } else if (closingTime === 'indefinitely') {
      closedUntil = addYears(new Date(), 10)
    } else {
      const endTime = config.value.workingTime!.end.split(':')
      const endHour = parseInt(endTime[0])
      const endMinute = parseInt(endTime[1])
      closedUntil = set(new Date(), {
        hours: endHour,
        minutes: endMinute
      })
      if (isBefore(closedUntil, new Date())) {
        closedUntil = addDays(closedUntil, 1)
      }
    }
    const closedUntilISO = formatISO(closedUntil, { representation: 'complete' })

    const { failed } = await deliveriesApi.closeDeliveries({
      closeDeliveriesRequest: {
        deliveriesToClose,
        closedUntil: closedUntilISO
      }
    })
    const failedKeys = new Set(failed.map(f => `${f.brandId}:${f.company}`))
    for (const [brandId, companies] of Object.entries(deliveriesToClose)) {
      for (const company of companies) {
        if (failedKeys.has(`${brandId}:${company}`)) continue
        const targetBrand = virtualBrandsClosingTimes.value.find(brand => brand.id === brandId)
        if (targetBrand) {
          targetBrand.externalDeliveryCompaniesClosingTimes[company] = closedUntilISO
        }
      }
    }
    return { closedUntil: closedUntilISO, failed }
  }

  return {
    virtualBrandsClosingTimes,
    sortedClosedUntil,
    virtualBrandsWithClosedDeliveries,
    hasDeliveries,
    bookingsBrand,

    refreshClosingTimes,
    openDeliveries,
    closeDeliveries
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDeliveryCompaniesStore, import.meta.hot))
}
